// Revolver.ts
import {AjaxP, PubSub} from '@mamdev/cad.static-next-lib';
import {html, nothing, render, type TemplateResult} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {range} from 'lit/directives/range.js';
import {map} from 'lit/directives/map.js';
import Tracking2 from '../../ts/services/tracking2';
import {type NextVideo} from '../../ts/services/video-player.types';
import type {EventData} from '../../ts/services/tracking2/interface';
import Viewport from '../../ts/services/viewport';
import Accessibility from '../../ts/services/accessibility';

export type RevolverTrigger = 'autoplay' | 'more-videos-button' | 'more-videos-notification';

const consoleLog = (...args: unknown[]) => console.log('[newvideo-revolver]', ...args);
// const consoleWarn = (...args: unknown[]) => console.warn('[newvideo-revolver]', ...args);
const consoleError = (...args: unknown[]) => console.error('[newvideo-revolver]', ...args);

const countdownTimerShowDelayBeforeUserInteraction = 1;
const countdownTimerShowDelayAfterUserInteraction = 3;
const countdownTimerMax = 5;

export class Revolver {
  private readonly _nextVideos: NextVideo[] = [];
  private readonly storageKey = 'viewedVideos';
  private readonly storageMaxItems = 20;
  private revolverPopup: HTMLElement | null;
  private readonly keyForNextVideo = 'revNextVideoId';
  private watchedVideoList: string[] = [];
  private readonly currentCoremId: string;
  private readonly componentPath: string;
  private additionalComponentPath = '';
  private readonly root_: HTMLElement;
  private countdownTimer: number | undefined;
  private _secondsToNextVideo = countdownTimerMax + countdownTimerShowDelayBeforeUserInteraction;
  private observer: IntersectionObserver;
  private wrapperElement: HTMLElement | null = null;
  private stageElement: HTMLElement | null = null;
  private countdownTimeToDisplay = 0;
  private timerPaused = false;
  private renderScheduled = false;
  private readonly log = consoleLog;

  get nextVideos() {
    return this._nextVideos;
  }

  get secondsToNextVideo() {
    return this._secondsToNextVideo;
  }

  set secondsToNextVideo(secondsToNextVideo: number) {
    if (secondsToNextVideo < 0) secondsToNextVideo = 0;

    this._secondsToNextVideo = secondsToNextVideo;

    // this.log('seconds to next video', this._secondsToNextVideo);

    try {
      if (secondsToNextVideo <= countdownTimerMax) {
        this.countdownTimeToDisplay = secondsToNextVideo;
      }

      this.scheduleRendering();

      if (secondsToNextVideo <= 0) {
        // this.log('load next video');
        this.trackAutoplay();
        this.jumpToNextVideo(this.nextVideos[0]);
      }
    } catch (e) {
      this.logError(e);
    }
  }

  constructor(root: HTMLElement, currentCoremId: string, componentPath: string, logFunction = consoleLog) {
    this.root_ = root;
    this.currentCoremId = currentCoremId;
    this.componentPath = componentPath;
    this.log = logFunction;
  }

  /**
   * Initialize revolver.
   */
  init(nextUrl: string): void {
    try {
      this.log('init');

      if (nextUrl.length < 1) {
        this.logError('missing URL');
        return;
      }

      this.updateWatched();
      this.loadData(nextUrl);
      this.log('Revolver initialized for url: ' + nextUrl);
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * updateWatched
   * add coremids for videos that have been watched
   */
  updateWatched(): void {
    let watched = window.sessionStorage.getItem(this.storageKey) ?? '';
    const watchedArray = watched.length > 0 ? watched.split(',') : [];

    if (!watchedArray.includes(this.currentCoremId)) {
      // current video hasn't been watched yet.
      watchedArray.push(this.currentCoremId);

      if (watchedArray.length > this.storageMaxItems) {
        watchedArray.shift();
      }

      watched = watchedArray.join(',');

      this.watchedVideoList = watchedArray;
      window.sessionStorage.setItem(this.storageKey, watched);
    }
  }

  /**
   * Load revolver data
   */
  loadData(url: string): void {
    // this.log(url + '?alreadySeen=' + this.watchedVideoList.join(','));

    AjaxP.getJSON<NextVideo[]>(url, {alreadySeen: this.watchedVideoList.join(',')})
      .then(nextVideos => {
        if (nextVideos) {
          // this.log('next videos', JSON.stringify(nextVideos));

          this.updateVideoList(nextVideos);

          // set next video id
          window.sessionStorage.setItem(this.keyForNextVideo, nextVideos[0].coremId);
          this.renderPopup();
        }
      })
      .catch(e => this.logError(e));
  }

  /**
   * Updates the list of videos that will be presented to the user.
   */
  updateVideoList(videos: NextVideo[]) {
    videos.forEach(video => {
      this.nextVideos.push({
        ...video,
        clickSuffix: video.clickSuffix ?? ''
      });
    });
  }

  /**
   * Render popup window
   */
  private renderPopup(): void {
    this.render();

    this.revolverPopup = this.root_.querySelector('.revolver');
    this.wrapperElement = this.root_.querySelector('.revolver__wrapper');
    this.stageElement = this.root_.querySelector('.revolver__stage');

    this.initEvents();
    this.initIntersectionObserver();
  }

  /**
   * Set popup events
   */
  initEvents(): void {
    // this.log('initEvents');

    this.revolverPopup?.querySelectorAll('.revolver__video').forEach(videoElement => {
      videoElement.addEventListener('click', e => {
        // if (this.popupClosed) return;

        const clickedElement = e.target as HTMLElement;
        const index = parseInt(clickedElement.dataset.revolverIndex ?? '');
        const video = this.nextVideos[index];

        if (this.componentPath) {
          this.trackEvent('userAction', video);
        }

        this.jumpToNextVideo(video);
      });
    });

    // reset timer if keyboard interaction happened
    PubSub.subscribe('accessibility:focusChanged', () => {
      this.resetCountdown();
    });

    // Listen for Escape key to close popup
    PubSub.subscribe('accessibility:escape', element => {
      if (element instanceof HTMLElement && element === this.revolverPopup) {
        this.closePopup(true);
      }
    });
  }

  /**
   * Shows the popup and starts the countdown timer if enabled.
   */
  showPopup(trigger: RevolverTrigger, countdownTimerEnabled = true, brainVidUrl: string) {
    if (!this.revolverPopup) return;

    const showCls = 'revolver-shown';

    if (this.revolverPopup.classList.contains(showCls)) return;

    // Keep track of what caused Revolver to show up, for tracking purposes.
    if (trigger === 'more-videos-button') {
      this.additionalComponentPath = '.morebutton';
    } else if (trigger === 'more-videos-notification') {
      this.additionalComponentPath = '.moreoverlay';
    } else {
      this.additionalComponentPath = '';
    }

    if (this.componentPath) {
      const teasers = this.revolverPopup?.querySelectorAll('.revolver__video');

      if (teasers) {
        Tracking2.bindTrackingClickHandler(teasers);
      }
    }

    this.revolverPopup.classList.add(showCls);
    this.scheduleRendering();

    // Use Accessibility service to trap focus
    Accessibility.trapFocus(this.revolverPopup, this.stageElement);

    PubSub.publish('video:setPixel', {piUrl: brainVidUrl, videoaction: '&videoaction=teaser-start'});

    Tracking2.callTealiumLink({
      businessEventType: 'expose',
      componentPath: `${this.componentPath}${this.additionalComponentPath}.revolver`,
      componentName: 'video.revolverplay'
    });

    if (countdownTimerEnabled) {
      if (this.countdownTimer) window.clearInterval(this.countdownTimer);

      this.countdownTimer = window.setInterval(() => {
        if (!this.timerPaused) this.secondsToNextVideo -= 1;
      }, 1000);
    }
  }

  /**
   * Tracks the autoplay of the first video.
   */
  trackAutoplay() {
    const trackingData: EventData = {
      businessEventType: 'technical.event',
      componentName: 'video.revolverplay',
      componentPath: `${this.componentPath}${this.additionalComponentPath}.revolver.video.1`,
      technicalEventInfo: {
        name: 'revolver.autoplay'
      },
      videoInfo: {
        articleId: `cmd:${this.nextVideos[0].coremId}`
      }
    };

    Tracking2.callTealiumLink(trackingData);
  }

  /**
   * Track an event via Tracking 2.0
   */
  private trackEvent(eventType: 'userAction' | 'expose', video: NextVideo) {
    const trackingData: EventData = {
      businessEventType: eventType,
      componentName: 'video.revolverplay',
      componentPath: `${this.componentPath}${this.additionalComponentPath}.teaser`,
      elementTagName: 'div',
      teasersInfo: {
        teaserBlockItems: [{
          articleId: `cmd:${video.coremId}`,
          teaserItemHeadline: video.headline,
          teaserItemSource: video.dataSource
        }]
      }
    };

    if (eventType === 'userAction') {
      trackingData.targetUrl = video.url;
      trackingData.eventType = 'mousedown';
    }

    Tracking2.callTealiumLink(trackingData);
  }

  /**
   * Close popup
   */
  closePopup(isUserAction = false): void {
    this.log('closePopup');

    window.clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;

    PubSub.publish('revolver:close');

    if (this.revolverPopup) {
      this.revolverPopup.classList.remove('revolver-shown');
      this.resetCountdown(true);

      Accessibility.releaseFocus();

      if (isUserAction) {
        Tracking2.callTealiumLink({
          businessEventType: 'userAction',
          componentPath: `${this.componentPath}${this.additionalComponentPath}.revolver.close-icon`,
          componentName: 'video.revolverplay',
          componentResultState: 'closed',
          eventType: 'click'
        });
      }

      this.additionalComponentPath = '';
    }
  }

  /**
   * Pauses the countdown timer.
   */
  pauseTimer() {
    this.timerPaused = true;
  }

  /**
   * Resumes the countdown timer.
   */
  resumeTimer() {
    this.timerPaused = false;
  }

  /**
   * Jumps to the next video.
   */
  private jumpToNextVideo(video: NextVideo): void {
    const trackingSuffix = video.clickSuffix;

    // this.log('jumpToNextVideo, next video url: ' + video.url);

    if (video.url.length > 0) {
      PubSub.publish('relatedvideo:setclick', {
        destination: video.url,
        brainData: '.' + this.currentCoremId + '.revolverPlayBtn.revolverPlayBtn' + trackingSuffix + '.1'
      });

      window.setTimeout(() => {
        window.location.href = video.url;
      }, 100);
    }
  }

  /**
   * Initializes the intersection observer.
   */
  private initIntersectionObserver() {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('faded');

          if (!entry.target.hasAttribute('data-exposed')) {
            const videoIndex = parseInt(entry.target.getAttribute('data-revolver-video-index') ?? '');

            Tracking2.callTealiumLink({
              businessEventType: 'expose',
              componentPath: `${this.componentPath}${this.additionalComponentPath}.revolver.video.${videoIndex + 1}`,
              componentName: 'video.revolverplay',
              videoInfo: {
                articleId: this.nextVideos[videoIndex].coremId
              }
            });

            entry.target.setAttribute('data-exposed', 'true');
          }
        } else {
          entry.target.classList.add('faded');
        }
      });
    }, {
      root: this.wrapperElement,
      threshold: 0.5
    });

    this.root_.querySelectorAll('.revolver__video').forEach(video => {
      this.observer.observe(video);
    });
  }

  /**
   * Logs an error in the console and in Sentry.
   */
  private logError(e: unknown) {
    consoleError(e);
    PubSub.publish('sentry:log', {error: e, module: 'newvideo-revolver'});
  }

  /**
   * Handles a click event on a teaser.
   */
  private handleTeaserClick(e: MouseEvent, video: NextVideo) {
    e.preventDefault();
    // this.log(video);
    this.jumpToNextVideo(video);
  }

  /**
   * Handles clicks on the fullscreen button.`
   * @private
   */
  private handleFullscreenButtonClick() {
    this.resetCountdown();
    PubSub.publish('revolver:toggle-fullscreen');
  }

  /**
   * Resets the countdown timer to its initial value.
   */
  resetCountdown(useInitialCountdownTimerDelay = false) {
    // this.log('reset countdown');
    this.secondsToNextVideo = countdownTimerMax + (useInitialCountdownTimerDelay ? countdownTimerShowDelayBeforeUserInteraction : countdownTimerShowDelayAfterUserInteraction);
  }

  /**
   * Determines whether the specified navigation element is to be highlighted or not, depending on the scroll amount of the teaser stage.
   */
  isNavigationElementToHighlight(elementIndex: number, navigationElementsAmount: number, stage: HTMLElement | null): boolean {
    if (!stage) return false;

    const scrollAmount = stage.scrollLeft ?? 0;
    const scrollMax = (stage.scrollWidth ?? 0) - (stage.offsetWidth ?? 0);
    const scrollSectionLength = scrollMax / navigationElementsAmount;
    const currentScrollSectionMin = scrollSectionLength * elementIndex;
    const currentScrollSectionMax = scrollSectionLength * (elementIndex + 1);

    // this.log('isNavigationElementToHighlight', elementIndex, navigationElementsAmount, `scrollAmount=${scrollAmount}`, `stage.scrollWidth=${stage.scrollWidth}`, `stage.offsetWidth=${stage.offsetWidth}`, `scrollMax=${scrollMax}`, `scrollSectionLength=${scrollSectionLength}`, (scrollAmount >= currentScrollSectionMin) && (scrollAmount < currentScrollSectionMax));

    return (scrollAmount >= currentScrollSectionMin) && (scrollAmount <= currentScrollSectionMax);
  }

  /**
   * Scrolls one viewport to the left.
   */
  scrollLeft(stage: HTMLElement | null) {
    if (stage) stage.scrollLeft -= (this.stageElement?.scrollWidth ?? 0) / this.calculateNumberOfStageSections();
  }

  /**
   * Scrolls one viewport to the right.
   */
  scrollRight(stage: HTMLElement | null) {
    if (stage) stage.scrollLeft += (this.stageElement?.scrollWidth ?? 0) / this.calculateNumberOfStageSections();
  }

  /**
   * Number of stage areas to display.
   */
  calculateNumberOfStageSections() {
    return Math.ceil((this.stageElement?.scrollWidth ?? 0) / this.root_.offsetWidth);
  }

  /**
   * Schedule a rendering operation to happen at the next animation frame.
   */
  scheduleRendering() {
    if (this.renderScheduled) return;

    this.renderScheduled = true;

    window.requestAnimationFrame(() => {
      this.render();
    });
  }

  /**
   * Updates the rendered template.
   */
  render() {
    render(this.renderTemplate(this.nextVideos, this.countdownTimeToDisplay), this.root_);
    this.renderScheduled = false;
  }

  /**
   * Renders the revolverPopup template.
   */
  private renderTemplate(nextVideos: NextVideo[], countdownTime: number): TemplateResult {
    const stage = this.stageElement;
    const navigationElementsAmount = this.calculateNumberOfStageSections();

    return html`
      <div class="revolver">
        <div class="revolver__wrapper"
             @mousedown="${() => this.resetCountdown()}">
          <div class="revolver__header">
            <div class="revolver__label">
              <span class="revolver__headline">Weitere Videos</span>
              <span class="revolver__countdown" style="opacity: ${this.secondsToNextVideo <= countdownTimerMax ? 1 : 0}">Nächstes startet in ${countdownTime}</span>
            </div>
          </div>

          <div class="revolver__stage"
               @scroll="${() => this.resetCountdown()}">
            ${nextVideos.map((video, index) => html`
              <a class="revolver__video"
                   href="${video.url}"
                   title="${video.headline}"
                   data-revolver-video-index="${index}"
                   data-component-name="video.revolverplay"
                   data-component-path="${this.componentPath}${this.additionalComponentPath}.revolver.video.${index + 1}"
                   @click="${(e: MouseEvent) => this.handleTeaserClick(e, video)}">
                <div class="revolver__thumb">
                  ${unsafeHTML(video.image)}
                </div>
                <div class="revolver__innerwrapper">
                  <div class="revolver__text">
                    <div class="revolver__video-kicker">${video.kicker}</div>
                    <div class="revolver__video-headline">${video.headline}</div>
                  </div>
                </div>
              </a>
            `)}

            <button class="revolver__arrow-left revolver-button" title="nach links navigieren" ?disabled=${stage?.scrollLeft === 0} @click="${() => this.scrollLeft(stage)}"></button>
            <button class="revolver__arrow-right revolver-button" title="nach rechts navigieren" ?disabled="${stage ? stage.scrollLeft >= (stage.scrollWidth - (stage.offsetWidth ?? 0)) : false}" @click="${() => this.scrollRight(stage)}"></button>
          </div>

          <div class="revolver__navigator">
            ${map(range(navigationElementsAmount), i => html`<span
                class="revolver__navigation-element ${this.isNavigationElementToHighlight(i, navigationElementsAmount, stage) ? 'highlighted' : ''}">
              </span>`
            )}
          </div>
        </div>

        <button class="revolver__close-button revolver-button" title="weitere Video Vorschläge schließen" @click="${() => this.closePopup(true)}">
          <span class="icon icon-close"></span>
        </button>

        ${Viewport.isIOSApp()
          ? nothing
          : html`<button class="revolver__fullscreen-button revolver-button" title="Vollbild Modus" @click="${() => this.handleFullscreenButtonClick()}"></button>`}
      </div>`;
  }
}
